import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  Button,
  IconButton,
  Divider,
  Paper,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  ShoppingBag as ShoppingBagIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
  FavoriteBorder,
  Security,
  CreditCard,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { cartApi } from '../api/cart';
import packageService from '../services/packageService';
import recommendationsService from '../services/recommendationsService';
import CheckoutStepper, { MiniCheckoutProgress } from '../components/CheckoutStepper';
// Removed ShippingCalculator - moved to Checkout page
import OrderSummary from '../components/OrderSummary';
import CTAButton from '../components/CTAButton';
import ProductCard from '../components/ProductCard';
import Seo from '../components/Seo';

// Product Image Placeholder Component - Export for use in other components
export const ProductImagePlaceholder = ({ size = 'medium' }) => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        borderRadius: size === 'small' ? 1 : 2,
      }}
    >
      <ImageIcon
        sx={{
          fontSize: size === 'small' ? 24 : 40,
          color: '#ccc',
          mb: 0.5,
        }}
      />
      <Typography
        sx={{
          fontSize: size === 'small' ? '0.7rem' : '0.85rem',
          color: '#999',
          fontWeight: 500,
        }}
      >
        No Image
      </Typography>
    </Box>
  );
};

const CartEnhanced = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [cartItems, setCartItems] = useState([]);
  const [failedImages, setFailedImages] = useState(new Set());
  const [isUpdatingPackage, setIsUpdatingPackage] = useState(false);
  const [clearCartConfirmOpen, setClearCartConfirmOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);


  const [recommendedProducts, setRecommendedProducts] = useState([]);

  const loadRecommendedProducts = async () => {
    try {
      const result = await recommendationsService.loadRecommendedProducts(1, 8);
      if (result.success) {
        setRecommendedProducts(result.data || []);
      }
    } catch (error) {
      console.error('Error loading recommended products:', error);
    }
  };

  useEffect(() => {
    loadCart();
    loadRecommendedProducts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const loadCart = () => {
    // Get only non-packaging items for display
    const items = cartApi.getNonPackagingItems();
    setCartItems(items);
  };

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity >= 0 && productId) {
      setIsUpdatingPackage(true);
      try {
        // Update the product quantity
        cartApi.updateQuantity(productId, newQuantity);

        // Recalculate package needs based on new total quantity
        if (newQuantity > 0) {
          // Calculate new total quantity (excluding packages)
          const nonPackageItems = cartApi.getNonPackagingItems();
          const newTotalQuantity = nonPackageItems.reduce((sum, item) => sum + (item?.quantity || 0), 0);

          // Update package if needed
          if (newTotalQuantity > 0) {
            await packageService.updatePackageIfNeeded(0, dispatch); // 0 because total is already calculated
          }
        }

        // Refresh cart display (only non-packaging items)
        const displayItems = cartApi.getNonPackagingItems();
        setCartItems(displayItems || []);
      } finally {
        setIsUpdatingPackage(false);
      }
    }
  };

  const handleRemoveItem = async (productId) => {
    if (productId) {
      setIsUpdatingPackage(true);
      try {
        // Remove the item
        cartApi.removeFromCart(productId);

        // Recalculate package needs after removal
        const nonPackageItems = cartApi.getNonPackagingItems();
        const newTotalQuantity = nonPackageItems.reduce((sum, item) => sum + (item?.quantity || 0), 0);

        // Update package if needed (or remove if cart is empty)
        if (newTotalQuantity > 0) {
          await packageService.updatePackageIfNeeded(0, dispatch);
        } else {
          // Remove all packages if cart is empty
          packageService.removePackagesFromCart();
        }

        // Refresh cart display (only non-packaging items)
        const displayItems = cartApi.getNonPackagingItems();
        setCartItems(displayItems || []);
      } finally {
        setIsUpdatingPackage(false);
      }
    }
  };

  const handleClearCartClick = () => {
    setClearCartConfirmOpen(true);
  };

  const handleClearCartConfirm = async () => {
    setClearCartConfirmOpen(false);
    setIsClearing(true);

    try {
      // Remove all items
      cartItems.forEach(item => {
        cartApi.removeFromCart(item?.product?.id || item?.product?._id || item?.id);
      });

      // Remove all packages if cart is empty
      packageService.removePackagesFromCart();

      // Clear cart display
      setCartItems([]);
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearCartCancel = () => {
    setClearCartConfirmOpen(false);
  };


  // Shipping is now handled in Checkout page


  // Calculate subtotal including additional services with proper null chaining
  const subtotal = cartItems.reduce((total, item) => {
    const basePrice = item?.product?.price || item?.product?.discountedPrice || item?.product?.normalPrice || item?.price || 0;
    const additionalServicesPrice = item?.product?.additionalServicesAmount || item?.additionalServicesAmount || 0;
    const itemTotal = (basePrice + additionalServicesPrice) * (item?.quantity || 1);
    return total + itemTotal;
  }, 0);

  // Get packaging fee from packaging items in cart
  const packagingFee = cartApi.getPackagingTotal();

  // Simplified cart total - tax and shipping will be calculated in checkout
  const total = subtotal + packagingFee;

  const orderItems = cartItems.map(item => ({
    id: item?.product?.id || item?.product?._id || item?.id,
    name: item?.product?.name || item?.product?.productName || item?.name || 'Product',
    quantity: item?.quantity || 1,
    price: item?.product?.price || item?.product?.discountedPrice || item?.product?.normalPrice || item?.price || 0,
    image: item?.product?.imageUrl || item?.product?.image || item?.product?.featuredImage || item?.product?.thumbailImagePath || item?.imageUrl,
    size: item?.product?.selectedSize || item?.selectedSize,
    additionalServicesAmount: item?.product?.additionalServicesAmount || item?.additionalServicesAmount || 0
  }));


  if (cartItems.length === 0) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: `
            linear-gradient(135deg, color-mix(in srgb, var(--primary) 3%, transparent) 25%, transparent 25%),
            linear-gradient(225deg, color-mix(in srgb, var(--primary) 3%, transparent) 25%, transparent 25%),
            linear-gradient(45deg, color-mix(in srgb, var(--primary) 3%, transparent) 25%, transparent 25%),
            linear-gradient(315deg, color-mix(in srgb, var(--primary) 3%, transparent) 25%, transparent 25%)
          `,
          backgroundPosition: '20px 0, 20px 0, 0 0, 0 0',
          backgroundSize: '40px 40px',
          backgroundRepeat: 'repeat',
          py: 8,
        }}
      >
        <Seo title="Shopping Cart" noindex />
        <Container maxWidth="lg">
          {/* Empty Cart Section - Centered */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 8 }}>
            <Box sx={{ maxWidth: 'sm', width: '100%', textAlign: 'center' }}>
            {/* Empty Cart Icon */}
            <Box
              sx={{
                width: 180,
                height: 180,
                mx: 'auto',
                mb: 4,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Outer Circle with Gradient */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, transparent) 0%, color-mix(in srgb, var(--primary) 5%, transparent) 100%)',
                  animation: 'pulse 3s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      transform: 'scale(1)',
                      opacity: 0.5,
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                      opacity: 0.8,
                    },
                  },
                }}
              />
              
              {/* Inner Circle */}
              <Box
                sx={{
                  position: 'absolute',
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ShoppingCartIcon 
                  sx={{ 
                    fontSize: 70, 
                    color: 'var(--primary)',
                    opacity: 0.9,
                  }} 
                />
              </Box>
              
              {/* Decorative Dots */}
              <Box
                sx={{
                  position: 'absolute',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary)',
                  top: 20,
                  right: 30,
                  opacity: 0.6,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary)',
                  bottom: 25,
                  left: 25,
                  opacity: 0.4,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary)',
                  top: 50,
                  left: 10,
                  opacity: 0.3,
                }}
              />
            </Box>
            
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                color: '#1a1a1a',
                mb: 2,
                fontSize: { xs: '1.8rem', sm: '2.125rem' },
              }}
            >
              Your cart is empty
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4, 
                maxWidth: 450, 
                mx: 'auto',
                color: '#666',
                fontSize: '1.05rem',
                lineHeight: 1.6,
              }}
            >
              Looks like you haven't added anything to your cart yet. Explore our amazing collection of sports jerseys and gear!
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/products')}
                startIcon={<ShoppingBagIcon />}
                size="large"
                sx={{
                  backgroundColor: 'var(--button-primary)',
                  px: 4,
                  py: 1.5,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': { 
                    backgroundColor: 'var(--button-hover)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 30%, transparent)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Start Shopping
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => navigate('/sale')}
                size="large"
                sx={{
                  borderColor: 'var(--primary)',
                  color: 'var(--primary)',
                  px: 4,
                  py: 1.5,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': { 
                    borderColor: 'var(--button-hover)',
                    backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                  },
                }}
              >
                View Sale Items
              </Button>
            </Box>
            
            {/* Popular Categories Quick Links */}
            <Box sx={{ mt: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Popular Categories
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                {['Football Jerseys', 'Basketball', 'Running', 'Training'].map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    onClick={() => navigate('/products')}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                        color: 'var(--primary)',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
            </Box>
          </Box>
          
          {/* You Might Also Like Section - Below Empty Cart (hidden when empty) */}
          {recommendedProducts.length > 0 && (
          <>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              You Might Also Like
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete your look with these handpicked items based on your shopping preferences
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {recommendedProducts.slice(0, 4).map((product) => (
              <Grid key={product.id} item xs={12} sm={6} md={3}>
                <ProductCard
                  product={product}
                  onAddToCart={() => {
                    cartApi.addToCart(product);
                    loadCart();
                  }}
                />
              </Grid>
            ))}
          </Grid>
          </>
          )}
        </Container>
      </Box>
    );
  }

  return (
    <>
      <Seo title="Shopping Cart" noindex />
      <Container maxWidth="lg" sx={{ py: 4, pb: isMobile ? 12 : 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            underline="hover"
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
          >
            Home
          </Link>
          <Typography color="text.primary">Shopping Cart</Typography>
        </Breadcrumbs>

        {/* Page Header with Progress */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
            Shopping Cart
            <Chip 
              label={`${cartItems.length} item${cartItems.length > 1 ? 's' : ''}`}
              size="small"
              sx={{ ml: 2 }}
            />
          </Typography>
          {isMobile && <MiniCheckoutProgress activeStep={0} />}
        </Box>

        {/* Checkout Stepper - Desktop Only */}
        {!isMobile && (
          <CheckoutStepper activeStep={0} />
        )}

        {/* Trust Badges */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Alert 
                icon={<LocalShippingIcon />} 
                severity="info"
                sx={{ py: 0.5 }}
              >
                Free shipping over KSH 5,000
              </Alert>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Alert 
                icon={<Security />} 
                severity="success"
                sx={{ py: 0.5 }}
              >
                Secure checkout
              </Alert>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Alert 
                icon={<CheckCircleIcon />} 
                severity="info"
                sx={{ py: 0.5 }}
              >
                30-day returns
              </Alert>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={3}>
          {/* Cart Items */}
          <Grid item xs={12} lg={8}>
            {/* Cart Items Header */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                mb: 2, 
                backgroundColor: '#f8f8f8',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Your Items ({cartItems.length})
                </Typography>
                <Button
                  size="small"
                  onClick={handleClearCartClick}
                  disabled={isClearing}
                  sx={{ color: '#666' }}
                >
                  Clear Cart
                </Button>
              </Box>
            </Paper>

            {/* Individual Cart Item Cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {cartItems.map((item) => (
                <Card 
                  key={item.product.id}
                  elevation={1}
                  sx={{ 
                    overflow: 'visible',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    <Grid container spacing={2}>
                      {/* Product Image */}
                      <Grid item xs={12} sm={3} md={2}>
                        <Box
                          sx={{
                            width: '100%',
                            height: { xs: 150, sm: 120 },
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid #f0f0f0',
                            position: 'relative',
                          }}
                        >
                          {(() => {
                            const productId = item?.product?.id || item?.product?._id || item?.id;
                            const imageUrl = item.product.imageUrl || item.product.image || item.product.featuredImage || item.product.thumbailImagePath || item.imageUrl;

                            // Check if we should show placeholder
                            if (!imageUrl || failedImages.has(productId)) {
                              return <ProductImagePlaceholder />;
                            }

                            return (
                              <Box
                                component="img"
                                src={imageUrl}
                                alt={item.product.name || item.product.productName || item.name || 'Product'}
                                onError={() => {
                                  // Mark this image as failed
                                  setFailedImages(prev => new Set([...prev, productId]));
                                }}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            );
                          })()}
                        </Box>
                      </Grid>

                      {/* Product Details */}
                      <Grid item xs={12} sm={9} md={10}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                          {/* Top Section: Name, Category, Delete */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontSize: { xs: '1rem', sm: '1.125rem' },
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                {item.product.name || item.product.productName || item.name || 'Product'}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Chip
                                  label={item.product.category || item.category || 'General'}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#e8f5f4',
                                    color: 'var(--primary)',
                                    fontWeight: 500,
                                  }}
                                />

                                {/* Display selected variations */}
                                {item.product?.selectedVariations && typeof item.product.selectedVariations === 'object' && Object.entries(item.product.selectedVariations).map(([key, value]) => {
                                  if (value) {
                                    return (
                                      <Chip
                                        key={key}
                                        label={`${key}: ${value}`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    );
                                  }
                                  return null;
                                })}

                                {/* Fallback to variations if selectedVariations not available */}
                                {(!item.product?.selectedVariations || Object.keys(item.product?.selectedVariations || {}).length === 0) && item.product?.variations && typeof item.product.variations === 'object' && Object.entries(item.product.variations).map(([key, value]) => {
                                  if (value) {
                                    return (
                                      <Chip
                                        key={key}
                                        label={`${key}: ${value}`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    );
                                  }
                                  return null;
                                })}

                                {/* Fallback to selectedSize if no variations available */}
                                {(!item.product?.selectedVariations || Object.keys(item.product?.selectedVariations || {}).length === 0) && (!item.product?.variations || Object.keys(item.product?.variations || {}).length === 0) && item.product?.selectedSize && (
                                  <Chip
                                    label={`Size: ${item.product.selectedSize}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}

                                {item.product.stock <= 5 && (
                                  <Chip
                                    label={`Only ${item.product.stock} left`}
                                    size="small"
                                    color="warning"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}

                                {/* Pre-Order Badge */}
                                {item.product?.isPreOrder && (
                                  <Chip
                                    label="PRE-ORDER"
                                    size="small"
                                    sx={{
                                      backgroundColor: '#F59E0B',  // Warning Orange from template
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.05em',
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Display Additional Services if any */}
                              {(item.product?.selectedAdditionalServices || item.product?.additionalServices) && (item.product?.selectedAdditionalServices?.length > 0 || item.product?.additionalServices?.length > 0) && (
                                <Box sx={{ mt: 1.5, p: 1, backgroundColor: '#f8f8f8', borderRadius: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                                    Additional Services:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(item.product?.selectedAdditionalServices || item.product?.additionalServices)?.map((service, index) => (
                                      <Box key={index}>
                                        {service.items?.map((serviceItem) => (
                                          <Chip
                                            key={serviceItem.itemId}
                                            label={`${service.serviceName}: ${serviceItem.itemName || serviceItem.itemValue || ''}`}
                                            size="small"
                                            sx={{
                                              backgroundColor: '#e8f5f4',
                                              color: 'var(--primary)',
                                              fontSize: '0.7rem',
                                            }}
                                          />
                                        ))}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                            <IconButton
                              onClick={() => handleRemoveItem(item?.product?.id || item?.product?._id || item?.id)}
                              size="small"
                              sx={{
                                color: '#666',
                                '&:hover': {
                                  color: '#d32f2f',
                                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                },
                              }}
                            >
                              <Tooltip title="Remove from cart">
                                <CloseIcon />
                              </Tooltip>
                            </IconButton>
                          </Box>

                          {/* Bottom Section: Quantity and Price */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2,
                            mt: 'auto',
                          }}>
                            {/* Quantity Controls */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                Quantity:
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  border: '2px solid #e0e0e0',
                                  borderRadius: 2,
                                  backgroundColor: '#fff',
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleUpdateQuantity(item?.product?.id || item?.product?._id || item?.id, (item?.quantity || 1) - 1)}
                                  disabled={(item?.quantity || 1) <= 1}
                                  sx={{ borderRadius: 0 }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Box
                                  sx={{
                                    px: 2,
                                    py: 0.5,
                                    minWidth: 40,
                                    textAlign: 'center',
                                    borderLeft: '2px solid #e0e0e0',
                                    borderRight: '2px solid #e0e0e0',
                                  }}
                                >
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {item?.quantity || 1}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleUpdateQuantity(item?.product?.id || item?.product?._id || item?.id, (item?.quantity || 1) + 1)}
                                  disabled={(item?.quantity || 1) >= 10}
                                  sx={{ borderRadius: 0 }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Price */}
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 700,
                                  color: '#000',
                                }}
                              >
                                KSH {(
                                  ((item?.product?.price || item?.product?.discountedPrice || item?.product?.normalPrice || item?.price || 0) +
                                   (item?.product?.additionalServicesAmount || item?.additionalServicesAmount || 0)) *
                                  (item?.quantity || 1)
                                ).toLocaleString()}
                              </Typography>
                              {item?.quantity > 1 && (
                                <Typography variant="caption" color="text.secondary">
                                  KSH {(
                                    (item?.product?.price || item?.product?.discountedPrice || item?.product?.normalPrice || item?.price || 0) +
                                    (item?.product?.additionalServicesAmount || item?.additionalServicesAmount || 0)
                                  ).toLocaleString()} per item
                                </Typography>
                              )}
                              {(item?.product?.additionalServicesAmount || item?.additionalServicesAmount || 0) > 0 && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'var(--primary)', mt: 0.5 }}>
                                  Incl. KSH {(item?.product?.additionalServicesAmount || item?.additionalServicesAmount || 0).toLocaleString()} additional services
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Quick Actions */}
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              startIcon={<FavoriteBorder />}
                              sx={{ 
                                color: '#666',
                                '&:hover': {
                                  color: 'var(--primary)',
                                  backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                                },
                              }}
                            >
                              Move to Wishlist
                            </Button>
                            <Button
                              size="small"
                              onClick={() => navigate(`/products/${item?.product?.id || item?.product?._id || item?.id}`)}
                              sx={{
                                color: '#666',
                                '&:hover': {
                                  color: 'var(--primary)',
                                  backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                                },
                              }}
                            >
                              View Product
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Card>
              ))}
            </Box>
          </Grid>

          {/* Order Summary Sidebar */}
          <Grid item xs={12} lg={4}>

            <OrderSummary
              items={orderItems}
              subtotal={subtotal}
              packagingFee={packagingFee}  // From packaging products in cart
              total={total}
              showDetails={true}
              showPolicies={true}
              isUpdatingPackage={isUpdatingPackage}
              hideShipping={true}  // Hide shipping in cart
              hideTax={true}  // Hide tax in cart
            />
            
            {/* Action Buttons */}
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <CTAButton
                variant="primary"
                size="large"
                fullWidth
                icon="checkout"
                onClick={() => {
                  navigate('/checkout');
                }}
              >
                Proceed to Checkout
              </CTAButton>
              
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ShoppingBagIcon />}
                onClick={() => navigate('/products')}
                sx={{
                  borderColor: '#e0e0e0',
                  color: '#666',
                  '&:hover': {
                    borderColor: 'var(--primary)',
                    backgroundColor: 'color-mix(in srgb, var(--primary) 4%, transparent)',
                  },
                }}
              >
                Continue Shopping
              </Button>

              {/* Payment Methods */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 1,
                textAlign: 'center' 
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  We accept
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <CreditCard sx={{ color: '#666' }} />
                  <Typography variant="caption">Visa • Mastercard • M-Pesa • PayPal</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* You Might Also Like Section (hidden when there is nothing to recommend) */}
        {recommendedProducts.length > 0 && (
        <Box sx={{ mt: 6, mb: 4 }}>
          <Divider sx={{ mb: 4 }} />
          
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 1, 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.125rem' },
                color: '#000',
              }}
            >
              You Might Also Like
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 600,
                mx: 'auto',
                fontSize: '0.95rem',
              }}
            >
              Complete your look with these handpicked items based on your shopping preferences
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 1, sm: 2, md: 2.5 }}>
            {recommendedProducts.map((product) => (
              <Grid key={product.id} item xs={6} sm={4} md={3} lg={2}>
                <ProductCard 
                  product={product} 
                  onAddToCart={() => loadCart()}
                />
              </Grid>
            ))}
          </Grid>

          {/* View All Products Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/products')}
              endIcon={<ShoppingBagIcon />}
              sx={{
                px: 4,
                backgroundColor: 'var(--button-primary)',
                '&:hover': {
                  backgroundColor: 'var(--button-hover)',
                },
              }}
            >
              View All Products
            </Button>
          </Box>
        </Box>
        )}
      </Container>

      {/* Mobile Sticky Bottom Bar */}
      {isMobile && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            borderTop: '1px solid #e0e0e0',
            zIndex: 1000,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              KSH {total.toLocaleString()}
            </Typography>
          </Box>
          <CTAButton
            variant="primary"
            size="large"
            fullWidth
            icon="checkout"
            onClick={() => navigate('/checkout')}
          >
            Proceed to Checkout
          </CTAButton>
        </Paper>
      )}

      {/* Clear Cart Confirmation Dialog */}
      <Dialog
        open={clearCartConfirmOpen}
        onClose={handleClearCartCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 380,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            bgcolor: alpha('#f44336', 0.08),
            borderBottom: '2px solid',
            borderColor: 'divider',
            pb: 2,
          }}
        >
          Clear Entire Cart?
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Are you sure you want to clear your entire shopping cart? This action cannot be undone. All your items will be removed.
          </Typography>
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: alpha('#f44336', 0.05),
              borderLeft: '4px solid #f44336',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500 }}>
              ⚠️ You have {cartItems.length} item{cartItems.length !== 1 ? 's' : ''} in your cart worth KSH {total.toLocaleString()}.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            pt: 2,
            px: 3,
            pb: 2,
            gap: 1,
          }}
        >
          <Button
            onClick={handleClearCartCancel}
            variant="outlined"
            disabled={isClearing}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearCartConfirm}
            variant="contained"
            color="error"
            disabled={isClearing}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              minWidth: 120,
            }}
          >
            {isClearing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                Clearing...
              </>
            ) : (
              'Clear Cart'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CartEnhanced;